import axios from 'axios';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

const DEFAULT_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 5 * 60_000;

/**
 * Axios instance for API communication
 *
 * Base URL is configured via VITE_API_BASE_URL environment variable.
 * Falls back to http://localhost:5000 for local development.
 *
 * Interceptors:
 * - Request: Adds authentication token from MSAL
 * - Response: Handles errors globally
 */
const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000',
	headers: {
		'Content-Type': 'application/json',
	},
	timeout: DEFAULT_TIMEOUT_MS,
});

/**
 * Token provider function
 * Set by AuthContext to provide access tokens for API calls
 */
let tokenProvider: (() => Promise<string | null>) | null = null;

/**
 * Set the token provider function
 * Called by AuthContext to enable automatic token injection
 */
export function setTokenProvider(provider: () => Promise<string | null>) {
	tokenProvider = provider;
}

/**
 * Request interceptor
 * Automatically adds Azure AD access token to all requests
 */
apiClient.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		// File uploads (a 30 MB document on a slow line) outlast the 30s default; the server finishes
		// the save anyway, so a client-side timeout only invites a duplicate on retry.
		if (config.data instanceof FormData && config.timeout === DEFAULT_TIMEOUT_MS) {
			config.timeout = UPLOAD_TIMEOUT_MS;
		}

		if (tokenProvider && !config.headers.Authorization) {
			// A provider that throws couldn't get a token (e.g. a network blip): fail the request here
			// rather than send it unauthenticated and read the 401 as "no access".
			const token = await tokenProvider();

			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
		}

		// Organisation scoping is per-request ONLY — see `scopeHeaders` in ./organisationScope.
		// Do NOT add an ambient X-Organisation-Id here: that header decides which org new rows land in.

		return config;
	},
	(error: AxiosError) => {
		return Promise.reject(error);
	},
);

/**
 * RFC 7807 Problem Details shape returned by the API.
 */
interface ProblemDetails {
	title?: string;
	detail?: string;
	errors?: Record<string, string[]>;
	/** Result<T>.Error: most controllers answer a failed command with `{ error }`, not Problem Details. */
	error?: string;
}

/**
 * Extracts a human-readable message from an RFC 7807 Problem Details payload.
 *
 * Priority:
 * 1. Validation errors (`errors` map) — flattened and joined
 * 2. `error` (the controllers' `{ error }` body)
 * 3. `detail`
 * 4. `title`
 *
 * Returns null when the payload has no usable message so the caller can
 * keep the default Axios message.
 */
function extractProblemDetailsMessage(data: unknown): string | null {
	if (!data || typeof data !== 'object') {
		return null;
	}

	const problem = data as ProblemDetails;

	if (problem.errors && typeof problem.errors === 'object') {
		const messages = Object.values(problem.errors)
			.flat()
			.filter((m): m is string => typeof m === 'string' && m.length > 0);

		if (messages.length > 0) {
			return messages.join('\n');
		}
	}

	if (typeof problem.error === 'string' && problem.error) {
		return problem.error;
	}

	if (problem.detail) {
		return problem.detail;
	}

	if (problem.title) {
		return problem.title;
	}

	return null;
}

/**
 * Response interceptor
 * Handles errors globally and provides consistent error formatting
 */
apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		return response;
	},
	(error: AxiosError) => {
		if (error.response) {
			// Server responded with error status
			const status = error.response.status;

			// Surface the API's Problem Details message via error.message so the
			// entire app (which reads error.message) shows the real reason.
			const message = extractProblemDetailsMessage(error.response.data);
			if (message) {
				error.message = message;
			}

			switch (status) {
				case 401:
					console.error('Unauthorized - token may have expired');
					// Token will be refreshed automatically by MSAL on next request
					break;
				case 403:
					console.error('Forbidden - insufficient permissions');
					break;
				case 404:
					console.error('Resource not found');
					break;
				case 500:
					console.error('Internal server error');
					break;
				default:
					console.error('API error:', error.response.data);
			}
		} else if (error.request) {
			// Request was made but no response received
			console.error('Network error - no response from server');
		} else {
			// Something else happened
			console.error('Request error:', error.message);
		}

		return Promise.reject(error);
	},
);

export default apiClient;
